import React, { useState, useEffect, createRef } from "react";
import PropTypes from "prop-types";
import cx from "classnames";

import { Portal } from "../Portal";

import CookieConsentMain from "./CookieConsentMain";
import CookieConsentDetail from "./CookieConsentDetail";
import CookieConsentHeadline from "./CookieConsentHeadline";
import CookieConsentText from "./CookieConsentText";
import CookieOption from "./CookieOption";
import CookieConsentContext from "./CookieConsentContext";
import CookieConsentViews from "./CookieConsentViews";

import { useFocusTrap } from "../hooks/useFocusTrap";

import styles from "./CookieConsent.scss";

export function CookieConsent({
  children,
  isOpen,
  isVisible: isVisibleProp,
  portalless,
  currentView: currentViewProp,
  privacyPolicyUrl,
  privacyPolicyCaption,
  acceptAllCaption,
  changeOptionsCaption,
  confirmSelectionCaption,
  onAcceptAll,
  onConfirmSelection,
  onChange,
  acceptedCookies: acceptedCookiesProp,
  zIndex,
  ...otherProps
}) {
  const childrenArray = React.Children.toArray(children);
  const [isVisible, setIsVisible] = useState(isVisibleProp);
  const modalRef = createRef();
  const { handleTabBehaviour } = useFocusTrap(modalRef);
  const PortalTag = portalless ? "div" : Portal;

  const [currentView, setCurrentView] = useState(currentViewProp);
  const [acceptedCookies, setAcceptedCookies] = useState(acceptedCookiesProp);

  useEffect(() => {
    setIsVisible(true);
    document.addEventListener("keydown", handleTabBehaviour);

    return () => {
      document.removeEventListener("keydown", handleTabBehaviour);
    };
  }, []);

  useEffect(() => {
    setCurrentView(currentViewProp);
  }, [currentViewProp]);

  useEffect(() => {
    setAcceptedCookies(acceptedCookiesProp);
  }, [acceptedCookiesProp]);

  const acceptCookie = (cookieName) => {
    if (acceptedCookies.includes(cookieName) === false) {
      setAcceptedCookies([...acceptedCookies, cookieName]);
      onChange([...acceptedCookies, cookieName]);
    }
  };

  const denyCookie = (cookieName) => {
    const updatedAcceptedCookies = acceptedCookies.filter(
      (cookie) => cookie !== cookieName
    );
    setAcceptedCookies(updatedAcceptedCookies);
    onChange(updatedAcceptedCookies);
  };

  const contextData = {
    acceptCookie,
    denyCookie,
    acceptedCookies,
  };

  const handleAcceptAll = () => {
    setCurrentView(CookieConsentViews.main);
    onAcceptAll();
  };

  const handleConfirmSelection = () => {
    setCurrentView(CookieConsentViews.main);
    onConfirmSelection(acceptedCookies);
  };

  const renderCurrentView = () => {
    const defaultViewProps = {
      onAcceptAll: handleAcceptAll,
      acceptAllCaption,
      privacyPolicyCaption,
      privacyPolicyUrl,
    };

    let viewElement;
    let viewProps;

    if (currentView === CookieConsent.View.detail) {
      // Detail
      viewElement = childrenArray.find(
        (child) => child.type === CookieConsentDetail
      );
      viewProps = {
        ...defaultViewProps,
        confirmSelectionCaption,
        onConfirmSelection: handleConfirmSelection,
      };
    } else {
      // Main
      viewElement = childrenArray.find(
        (child) => child.type === CookieConsentMain
      );
      viewProps = {
        ...defaultViewProps,
        setCurrentView,
        changeOptionsCaption,
      };
    }

    return React.cloneElement(viewElement, viewProps);
  };

  return (
    <PortalTag>
      <div
        className={cx(styles["outer-container"], {
          "is-visible": isOpen && isVisible,
        })}
        style={{ zIndex }}
        {...otherProps}
      >
        <div
          className={cx(styles.cover, { "is-visible": isOpen && isVisible })}
        />

        <div
          className={cx(styles.modal, { "is-visible": isOpen && isVisible })}
          ref={modalRef}
          role="dialog"
          aria-modal="true"
        >
          <div className={styles["inner-container"]}>
            <CookieConsentContext.Provider value={contextData}>
              <div className={styles.content}>{renderCurrentView()}</div>
            </CookieConsentContext.Provider>
          </div>
        </div>
      </div>
    </PortalTag>
  );
}

CookieConsent.View = CookieConsentViews;

CookieConsent.defaultProps = {
  isOpen: true,
  isVisible: false,
  currentView: CookieConsent.View.main,
  portalless: false,
  onAcceptAll: () => {},
  onConfirmSelection: () => {},
  onChange: () => {},
  acceptedCookies: [],
  privacyPolicyCaption: "Datenschutzhinweis",
  acceptAllCaption: "Alle akzeptieren",
  changeOptionsCaption: "Einstellungen ändern",
  confirmSelectionCaption: "Auswahl bestätigen",
  zIndex: "auto",
};

CookieConsent.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  isOpen: PropTypes.bool,
  isVisible: PropTypes.bool,
  portalless: PropTypes.bool,
  currentView: PropTypes.oneOf(Object.values(CookieConsent.View)),
  onAcceptAll: PropTypes.func,
  onConfirmSelection: PropTypes.func,
  onChange: PropTypes.func,
  acceptedCookies: PropTypes.arrayOf(PropTypes.string),
  privacyPolicyUrl: PropTypes.string.isRequired,
  privacyPolicyCaption: PropTypes.string,
  acceptAllCaption: PropTypes.string,
  changeOptionsCaption: PropTypes.string,
  confirmSelectionCaption: PropTypes.string,
  zIndex: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

CookieConsent.Main = CookieConsentMain;
CookieConsent.Detail = CookieConsentDetail;
CookieConsent.Headline = CookieConsentHeadline;
CookieConsent.Text = CookieConsentText;
CookieConsent.Option = CookieOption;

CookieConsent.displayName = "CookieConsent";

export default CookieConsent;
